import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Volume2, VolumeX } from 'lucide-react';
import { heroVideo } from '../data/mockData';
import { getImageUrl } from '../lib/utils';
import InteractiveProductImage from '../components/InteractiveProductImage';
import InstagramStorySection from '../components/InstagramStorySection';
import StaggerTestimonials from '../components/StaggerTestimonials';
import VintageLibraryShelf from '../components/VintageLibraryShelf';

const HeroSection = () => {
  const [isMuted, setIsMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Get full video URL
  const videoUrl = heroVideo.startsWith('/api/') 
    ? `${backendUrl}${heroVideo}` 
    : heroVideo;

  return (
    <div className="relative h-screen overflow-hidden bg-black">
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted={isMuted}
        playsInline
        preload="auto"
        onLoadedData={() => setVideoLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
      >
        <source src={videoUrl} type="video/mp4" />
      </video>
      
      {/* Loading placeholder */}
      {!videoLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          </div>
        </div>
      )}
      
      <div className="absolute inset-0 bg-black/30" />
      
      {/* Content */}
      <div className="relative h-full flex items-center justify-center text-center text-white px-6">
        <div className="max-w-2xl">
          <p className="text-sm tracking-[0.3em] mb-4 animate-fade-in-up">
            YENİ KOLEKSİYON
          </p>
          <h1 className="text-5xl md:text-7xl font-light tracking-wider mb-6 animate-fade-in-up animation-delay-200">
            Kökten Işığa
          </h1>
          <p className="text-lg font-light mb-8 opacity-90 animate-fade-in-up animation-delay-400">
            Doğanın en saf halleri, ipek üzerinde hayat buluyor
          </p>
          <Link
            to="/urunler"
            className="inline-block border border-white px-10 py-4 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 animate-fade-in-up animation-delay-600"
          >
            KOLEKSİYONU KEŞFET
          </Link>
        </div>
      </div>

      {/* Mute/Unmute Button */}
      <button
        onClick={() => setIsMuted(!isMuted)}
        className="absolute bottom-10 right-10 p-3 text-white bg-black/30 hover:bg-black/50 transition-colors rounded-full"
      >
        {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
      </button>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center text-white animate-bounce">
        <span className="text-xs tracking-wider mb-2">KAYDIR</span>
        <div className="w-px h-8 bg-white/50" />
      </div>
    </div>
  );
};

const CollectionBooksMini = () => {
  const covers = [
    { slug: 'kokten-isiga', name: 'Kökten Işığa', year: '2025', accent: '#8B7355', dark: '#2E2419', poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/yre7q9yc_KI.png' },
    { slug: 'turk-motifleri', name: 'Türk Motifleri', year: '2026', accent: '#2E86AB', dark: '#0F2A44', poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/3uga6a0c_TM%20Afis%CC%A76.png' },
    { slug: 'yaz-masali', name: 'Yaz Masalı', year: '2026', accent: '#E4A853', dark: '#4A2E14', poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/n60bnjxu_1.webp' },
    { slug: 'sakli-bahce', name: 'Saklı Bahçe', year: '2026', accent: '#4A7C59', dark: '#1A2E20', poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/9uhugh6k_Sakl%C4%B1%20Bahc%CC%A7e%20.png' },
    { slug: 'goc', name: 'Göç', year: '2026', accent: '#8B4A6B', dark: '#2A1421', poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/1mbqa4fp_Go%CC%88c%CC%A7%20Afis%CC%A7.png' },
    { slug: 'nazende', name: 'Nazende', year: '2026', accent: '#B08D57', dark: '#2B1E12', poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/qgsdb3qn_nazende.png' },
  ];

  return (
    <section className="py-20 md:py-24 bg-[#0f0803] text-white" data-testid="mini-collection-books">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-xs tracking-[0.5em] opacity-60 mb-4">— DİĞER KOLEKSİYONLAR —</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light">
            Küçük <span className="italic font-thin">Kitaplık</span>
          </h2>
          <p className="opacity-60 max-w-lg mx-auto mt-4 text-sm italic">
            Her kitap bir hikâye. Kapağa dokunun, hikâyenin sayfalarını açın.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {covers.map((c, i) => (
            <Link
              key={c.slug}
              to={`/koleksiyonlar/${c.slug}`}
              className="group relative"
              data-testid={`mini-book-${c.slug}`}
              style={{ perspective: '800px' }}
            >
              <div
                className="relative aspect-[2/3] overflow-hidden shadow-[0_15px_40px_-10px_rgba(0,0,0,0.7)] transition-transform duration-500 group-hover:-translate-y-2"
                style={{
                  background: `linear-gradient(135deg, ${c.dark} 0%, ${c.accent}22 100%)`,
                  borderRadius: '2px 8px 8px 2px',
                }}
              >
                {c.poster && (
                  <img
                    src={c.poster}
                    alt={c.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
                {/* Spine effect */}
                <div className="absolute left-0 top-0 bottom-0 w-2" style={{ background: `linear-gradient(to right, #0a0603 0%, ${c.accent}44 100%)` }} />
                {/* Corner marks */}
                <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2" style={{ borderColor: c.accent }} />
                <div className="absolute bottom-2 left-4 w-3 h-3 border-b-2 border-l-2" style={{ borderColor: c.accent }} />
                {/* Content */}
                <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-center">
                  <div className="text-[8px] tracking-[0.4em] opacity-70 mb-1">№ {String(i + 1).padStart(2, '0')} · {c.year}</div>
                  <div className="font-serif italic text-base md:text-lg leading-tight">{c.name}</div>
                </div>
              </div>
              <div className="text-center text-[10px] tracking-[0.3em] opacity-50 mt-3 group-hover:opacity-90 transition-opacity">
                → aç
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const CollectionsSection = () => (
  <section className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-sm tracking-[0.3em] text-gray-500 mb-4">YENİ KOLEKSİYON</p>
          <h2 className="text-4xl md:text-5xl font-light tracking-wider mb-6">Kökten Işığa</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Ülkemizde sökülen zeytinlikleri gördük, içimiz acıdı. O köklerin yerinden 
            sökülmesi bize çok tanıdık geldi — özellikle kadınlar için. O yüzden kadını 
            ve zeytini aynı kökten düşündük.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4 italic">
            "Biri toprağın bereketi, diğeri yaşamın özü."
          </p>
          <p className="text-gray-600 leading-relaxed mb-8">
            Ve bu hikâyeyi ipekle anlattık. Çünkü ipek, kadına yalnızca dokunuşuyla değil,
            ruhsal olarak da iyi gelir.
          </p>
          <Link
            to="/koleksiyonlar"
            className="inline-flex items-center gap-2 bg-black text-white px-10 py-4 text-sm tracking-wider hover:bg-gray-900 transition-colors"
          >
            HİKAYEYİ KEŞFET <ArrowRight size={16} />
          </Link>
        </div>
        <div className="relative">
          <Link to="/urun/7" data-testid="afife-jale-collection-link" className="block">
            <img
              src={getImageUrl('/api/files/valmera/uploads/1cc1c3b1838445fdb21347fd43afafec.png')}
              alt="Afife Jale – Perdenin İlk Adımı"
              className="w-full aspect-square object-cover"
            />
            <span
              data-testid="afife-jale-sale-badge"
              className="absolute top-4 right-4 flex items-center gap-2 bg-red-600 text-white text-xs font-semibold px-3 py-1.5 tracking-wider shadow-lg"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse"></span>
              AFİFE JALE SATIŞTA
            </span>
          </Link>
          <div className="absolute -bottom-6 -left-6 bg-black text-white p-6 max-w-xs">
            <p className="text-xl font-light mb-1">Kökten Işığa</p>
            <p className="text-sm opacity-80">Kadının ve zeytinin hikayesi</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FeaturedProducts = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/products`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedProducts(data.filter(p => p.isBestseller).slice(0, 4));
        }
      } catch (error) {
        console.error('Products fetch error:', error);
      }
    };
    fetchProducts();
  }, [backendUrl]);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-light tracking-wider mb-2">ÇOK SATANLAR</h2>
            <p className="text-gray-500">En çok tercih edilen parçalar</p>
          </div>
          <Link
            to="/urunler"
            className="hidden md:flex items-center gap-2 text-sm tracking-wider hover:gap-4 transition-all"
          >
            TÜM ÜRÜNLER <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featuredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <Link
          to="/urunler"
          className="flex md:hidden items-center justify-center gap-2 mt-10 text-sm tracking-wider"
        >
          TÜM ÜRÜNLER <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
};

const ProductCard = ({ product }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const displayPrice = product.prices?.['65x65'] || product.price;

  return (
    <Link to={`/urun/${product.id}`} className="group">
      <InteractiveProductImage product={product} className="mb-4">
        {product.isNew && (
          <span className="absolute top-4 left-4 bg-black text-white text-xs px-3 py-1 tracking-wider z-10">
            YENİ
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
          <button className="w-full bg-white text-black py-3 text-sm tracking-wider hover:bg-gray-100 transition-colors">
            HIZLI İNCELE
          </button>
        </div>
      </InteractiveProductImage>
      <div className="text-center">
        <h3 className="text-sm font-medium mb-2 group-hover:underline">{product.name}</h3>
        <div className="flex items-center justify-center gap-3">
          <span className="text-sm">
            {formatPrice(displayPrice)}
          </span>
        </div>
      </div>
    </Link>
  );
};

const NewArrivalsSection = () => {
  const [newProducts, setNewProducts] = useState([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/products`);
        if (response.ok) {
          const data = await response.json();
          setNewProducts(data.filter(p => p.isNew).slice(0, 4));
        }
      } catch (error) {
        console.error('Products fetch error:', error);
      }
    };
    fetchProducts();
  }, [backendUrl]);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-light tracking-wider mb-2">YENİ GELENLER</h2>
            <p className="text-gray-500">Sezonun en yeni tasarımları</p>
          </div>
          <Link
            to="/urunler"
            className="hidden md:flex items-center gap-2 text-sm tracking-wider hover:gap-4 transition-all"
          >
            TÜM YENİLER <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {newProducts.map((product) => (
            <ProductCard key={product.id} product={product} backendUrl={backendUrl} />
          ))}
        </div>
      </div>
    </section>
  );
};

const BrandStorySection = () => {
  const videos = [
    {
      src: 'https://customer-assets.emergentagent.com/job_valmera-luxury/artifacts/b8m7yiyj_Bug%C3%BCn%20bir%20markan%C4%B1n%20do%C4%9Fu%C5%9Funu%20de%C4%9Fil%2C%20bir%20hayalin%20ger%C3%A7ekle%C5%9Fmesini%20izliyorsunuz.%20Bu%20s%C3%BCrecin%20nas%C4%B1l%20ad.mp4',
      title: 'Bir Hayalin Gerçekleşmesi',
      desc: 'Bir markanın değil, bir hayalin doğuşu.',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_valmera-luxury/artifacts/r94l9oth_Beklenen%20kargo%20a%C3%A7%C4%B1l%C4%B1m%C4%B1%20%F0%9F%8E%81%F0%9F%A7%9A%F0%9F%8F%BB_%E2%99%80%EF%B8%8F%F0%9F%A5%B3Sizler%20i%C3%A7in%20fularlar%C4%B1m%C4%B1z%C4%B1n%2090x90%20e%C5%9Farp%20boyutunda%20da%20tasar%C4%B1mlar.mp4',
      title: '90×90 Tasarımlar',
      desc: 'Fularlarımız artık 90×90 eşarp boyutunda da sizinle.',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_valmera-luxury/artifacts/qpsh8fwe_%40valmeraofficial%20fularlar%C4%B1nda%20T%C3%BCrk%20Kad%C4%B1nlar%C4%B1n%C4%B1%20anlat%C4%B1yor.Safiye%20Ali%20tasar%C4%B1m%C4%B1m%C4%B1z%20sizinle......%23lu.mp4',
      title: 'Safiye Ali',
      desc: 'Türk kadınlarının hikâyesi, ipeğin üstünde.',
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-[#0a0a0a] text-white overflow-hidden" data-testid="brand-story-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14 md:mb-20">
          <div className="text-xs tracking-[0.5em] opacity-60 mb-4">— VALMÉRA HİKAYESİ —</div>
          <h2 className="font-serif text-4xl md:text-6xl font-light mb-6 leading-tight">
            Bir markanın değil,<br />
            <span className="italic font-thin">bir hayalin</span> doğuşu.
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto italic">
            Atölyeden ipeğe, ipekten kadına uzanan yolculuğun kayıtları.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {videos.map((v, i) => (
            <div key={i} className="group relative overflow-hidden bg-black" data-testid={`brand-video-${i}`}>
              <div className="relative aspect-[9/16] md:aspect-[3/4]">
                <video
                  src={v.src}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

                {/* Play overlay */}
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 backdrop-blur-sm px-2 py-1 border border-white/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[9px] tracking-[0.3em] text-white">CANLI</span>
                </div>

                {/* Bottom content */}
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <div className="text-[10px] tracking-[0.4em] text-amber-200/80 mb-2">
                    KAYIT {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl mb-2 leading-tight">{v.title}</h3>
                  <p className="text-white/70 text-sm">{v.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  return (
    <main>
      <HeroSection />
      <CollectionBooksMini />
      <CollectionsSection />
      <FeaturedProducts />
      <NewArrivalsSection />
      <StaggerTestimonials />
      <InstagramStorySection />
    </main>
  );
};

export default HomePage;
